/**
 * DenPersonaRequestModal — when a user clicks "My career isn't here yet"
 * (or chooses "Other" in the designation dropdown), this opens to capture
 * what they actually do. Writes to den_persona_requests.
 *
 * Closing the modal NEVER changes the user's active Den. The "Other"
 * silent un-Den bug was Saul's biggest UX complaint of the live test.
 */
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

interface Props {
  open: boolean;
  onClose: () => void;
}

export function DenPersonaRequestModal({ open, onClose }: Props) {
  const [career, setCareer] = useState("");
  const [why, setWhy] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!alive) return;
      if (user?.email) setEmail(user.email);
    })();
    return () => { alive = false; };
  }, [open]);

  async function submit() {
    if (!career.trim()) {
      toast.error("Tell us what you do — even a few words helps.");
      return;
    }
    setSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not signed in");
      const { error } = await supabase.from("den_persona_requests" as any).insert({
        user_id: user.id,
        career_description: career.trim(),
        why_helpful: why.trim() || null,
        email: email.trim() || null,
      });
      if (error) throw error;
      toast.success("Thanks — we'll let you know when your Den ships.");
      setCareer(""); setWhy("");
      onClose();
    } catch (e: any) {
      toast.error(`Couldn't submit: ${e?.message || "unknown error"}`);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Tell us about your career</DialogTitle>
          <DialogDescription>
            We don't have a Den for your work yet. Tell us what you do — we add new Dens roughly every two weeks. You'll keep your current Den; this is just a roadmap vote.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 mt-2">
          <label className="grid gap-1">
            <span className="text-[12px] font-medium text-foreground">What's your career?</span>
            <Input
              value={career}
              onChange={(e) => setCareer(e.target.value)}
              placeholder="e.g., wedding planner, Etsy seller, oncologist"
              disabled={submitting}
            />
          </label>
          <label className="grid gap-1">
            <span className="text-[12px] font-medium text-foreground">Why would a Den for this help you? <span className="text-muted-foreground">(optional)</span></span>
            <Textarea
              value={why}
              onChange={(e) => setWhy(e.target.value)}
              placeholder="What signals matter to you? What would the daily home page look like?"
              rows={3}
              disabled={submitting}
            />
          </label>
          <label className="grid gap-1">
            <span className="text-[12px] font-medium text-foreground">Email <span className="text-muted-foreground">(we'll ping you when it ships)</span></span>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@yourcompany.com"
              disabled={submitting}
            />
          </label>
        </div>
        <div className="flex items-center justify-end gap-2 mt-2">
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={submitting || !career.trim()}>
            {submitting ? "Submitting…" : "Submit request"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default DenPersonaRequestModal;
